import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Container,
  Box,
  Alert,
  Paper,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const UserEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();

  const [user, setUser] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role_id: '', // backend column name kept — semantically this is the permission_group id
  });

  // List of permission groups (formerly roles). The DB table is still named
  // `roles` so `/roles` returns the right rows; we just relabel everything in
  // the UI to match the Access Control v2 vocabulary.
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    axios
      .get(`${apiUrl}/users/${userId}`)
      .then((response) => {
        setUser(response.data);
      })
      .catch((error) => {
        console.error('Failed to fetch user details:', error);
        setError(t('failed_to_fetch_user'));
      });

    axios
      .get(`${apiUrl}/roles`)
      .then((response) => {
        setGroups(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error('Failed to fetch permission groups:', error);
      });
  }, [userId, t]);

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    axios
      .put(`${apiUrl}/users/${userId}`, user)
      .then(() => {
        setSuccess(t('user_updated_successfully'));
        setError('');
        setTimeout(() => {
          navigate('/user-list'); // إعادة توجيه بعد التحديث
        }, 2000);
      })
      .catch((error) => {
        console.error('Failed to update user:', error);
        setError(t('failed_to_update_user'));
      });
  };

  return (
    <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {t('edit_user')}
        </Typography>
        <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
          {t('edit_user_hint') || 'Update profile information and the assigned permission group.'}
        </Typography>
      </Paper>
      <Container component="main" maxWidth="md" disableGutters>
        <Paper component="form" elevation={0} onSubmit={handleSubmit} sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
          <Stack spacing={1}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label={t('username')}
            name="username"
            value={user.username}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="first_name"
            label={t('first_name')}
            name="first_name"
            value={user.first_name}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="last_name"
            label={t('last_name')}
            name="last_name"
            value={user.last_name}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('email')}
            name="email"
            value={user.email}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="phone"
            label={t('phone')}
            name="phone"
            value={user.phone}
            onChange={handleChange}
          />

          {/* Permission group selector */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('permission_group') || 'Permission group'}</InputLabel>
            <Select
              id="role_id"
              name="role_id"
              value={user.role_id || ''}
              onChange={handleChange}
              label={t('permission_group') || 'Permission group'}
            >
              {groups.map((group) => (
                <MenuItem key={group.role_id} value={group.role_id}>
                  {group.role_name} | {group.role_name_ar}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" sx={{ color: COLORS.muted, mt: 0.5, ml: 1.5 }}>
              {t('permission_group_hint') ||
                'Controls what this user can see and access across the system.'}
            </Typography>
          </FormControl>

          {/* رسائل النجاح والخطأ */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 3,
              mb: 2,
            }}
          >
            <Button type="submit" variant="contained" sx={{ width: '48%', bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
              {t('update')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/user-list')}
              sx={{ width: '48%' }}
            >
              {t('cancel')}
            </Button>
          </Box>
          </Stack>
        </Paper>
    </Container>
    </Box>
  );
};

export default UserEdit;
